import store from '..'
import axios from 'axios'
import router from '@/router'
import API from '@/common/api'
import moment from 'moment-timezone'
import {getCustomSite, applySiteCustomCss} from '@/common/applyCustomCss'

// google web font
const EXTRA_FONTS = [
  'Noto Sans SC', // 특정 폰트 (30개에 속하지 않는 구글폰트)
];

const WEIGHTED_FONTS = new Map([
  ['Noto Sans KR', [300, 400, 500, 600, 700]],
  ['Noto Sans SC', [300, 400, 500, 600, 700]],
]);

function toFamilyParamV2(family) {
  // v2는 공백을 +로
  const fam = family.trim().replace(/ /g, '+')

  // 가중치 지정 폰트는 :wght@... 형태 추가
  const weights = WEIGHTED_FONTS.get(family);
  if (weights && weights.length) {
    return `family=${fam}:wght@${weights.join(';')}`
  }
  return `family=${fam}`
}

function buildGoogleFontsCss2Url(families) {
  const uniq = Array.from(new Set(families.map(f => (f || '').trim()).filter(Boolean)))

  // v2는 family=...&family=... 로 이어붙임
  const params = uniq.map(toFamilyParamV2).join('&')

  // display=swap 포함
  return `https://fonts.googleapis.com/css2?${params}&display=swap`
}

const WEBFONT_LINK_ID = 'webfonts-css2'

axios
  .get('https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=AIzaSyD87kW7jzc3vPCMPA6SFTOglRHYvOb6D_o')
  .then(res => res.data.items)
  .then(items => {
    // 1) 상위 30개 + 내가 추가한 폰트 결합(중복 제거)
    const top30 = items.slice(0, 30).map(x => x.family)
    const allFamilies = Array.from(new Set([...top30, ...EXTRA_FONTS]))

    // 2) v2 URL 생성
    const href = buildGoogleFontsCss2Url(allFamilies)

    // 3) <link> 생성/교체 (중복 삽입 방지)
    const existing = document.getElementById(WEBFONT_LINK_ID)
    if (existing && existing.getAttribute('href') === href) return

    if (existing && existing.parentNode) existing.parentNode.removeChild(existing)

    const webfont = document.createElement('link')
    webfont.id = WEBFONT_LINK_ID
    webfont.rel = 'stylesheet'
    webfont.href = href
    document.head.appendChild(webfont)
  })
  .catch((ex) => {
    console.error('error:', ex)
  })


export default {
  namespaced: true,
  state: {
    lang: {},
    customSiteKey: null,
    setting: {
      mypage: {
        room: true,
        disabledRoom: false,
        disabledMypage: false,
        disabledCart: false,
        checkin: false,
        checkout: false,
        coupon: true,
        cart: true,
        order: true,
        payment: true,
        reservation: true,
        mobility: false
      },
    },
    menu: {
      list: []
    },
    floating: {},
    dgb: null,
    guestType: {}
  },
  mutations: {
    setMeta (state) {
      const title = state.setting && state.setting.title && state.setting.title[state.lang.current]
      if (!title) return
      document.title = title
    },
    setMetaScale (state, isScalable) {
      const scalable = isScalable ? 'user-scalable=yes' : 'user-scalable=no'
      const content = `width=device-width,initial-scale=1.0,${scalable}`
      document.querySelector('meta[name="viewport"]').setAttribute('content', content)
    },
    langObj (state, data) {
      const obj = {}
      state.setting.lang.info.forEach(function (x) {
        obj[x.c] = data || ''
      })
      return obj
    },
    setUsableLang (state) {
      const setting = state.setting.lang
      const langInfo = setting.info
      const langOrder = setting.langOrder || []
      const langs = setting ? [...langOrder, ...setting.usable.filter(x => !langOrder.includes(x))] : ['ko', 'en']
      state.setting.lang.usableList = langs.map(lang => {
        return langInfo.find(l => l.c === lang)
      }).filter(lang => lang && lang.u)
    }
  },
  actions: {
    setDocsLang ({ state }, lang) {
      document.getElementsByTagName('html')[0].setAttribute('lang', lang)
      const setLang = setInterval(() => {
        if (document.getElementsByTagName('html')[0].getAttribute('lang') !== lang) {
          document.getElementsByTagName('html')[0].setAttribute('lang', lang)
        } else {
          clearInterval(setLang)
        }
      }, 100)
    },
    async getGuestTypes ({ state }) {
      try {
        const res = await axios.post(`${API.BASE_URL}/castit/file`, {
          cmd: 'sc-manage',
          func: 'get-guest-types',
          id: `a.${store.state.authentication.site}`
        })
        return res.data.message
      } catch (ex) {
        return false
      }
    },
    async loadSetting({ dispatch }) {
      const file = 'setting/setting.json'

      const normalizeAndThrow = (ex) => {
        if (ex && ex.code) throw ex
        const msg = (ex && ex.response && ex.response.data && ex.response.data.message) || ''
        const reason = msg.includes('No file found')
          ? 'setting_not_found'
          : 'fail_to_load'
        const err = new Error(reason)
        err.code = reason
        err.raw = ex
        throw err
      }

      try {
        const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-file&siteid=${API.SITE_INFO.site}&file=${file}`)

        if (res && res.response) {
          normalizeAndThrow(res)
        }
        const msg = res && res.data && res.data.message
        if (msg) {
          dispatch('setCommon', msg)
          return msg
        }
        const err = new Error('setting_empty')
        err.code = 'setting_empty'
        throw err
      } catch (ex) {
        normalizeAndThrow(ex)
      }
    },
    async getSiteinfo ({ state }) {
      try {
        const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-site-info&siteid=${API.SITE_INFO.site}`)
        store.state.siteInfo = res.data.message
        const timezone = res.data.message.timezoneId
        moment.tz.setDefault(timezone)
        return res.data.message
      } catch (ex) {
        state.dgb = null
        return false
      }
    },
    async getDgb ({ state }) {
      try {
        const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-dgb-siteid&siteid=${API.SITE_INFO.site}`)
        return res.data.message
      } catch (ex) {
        state.dgb = null
        return false
      }
    },
    async loadDgbData ({ state }, dgbData) {
      const file = `setting/setting.json`
      if (dgbData.name !== 'localinfo') {
        try {
          const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-file&siteid=${dgbData.name}&file=${file}`)
          return res.data.message.main.page
        } catch (ex) {
          return null
        }
      }
    },
    getDgbData ({ state, dispatch }) {
      return dispatch('getDgb').then((res) => {
        if (res) {
          state.dgb = res
          dispatch('loadDgbData', res).then((res) => {
            state.dgb.page = res
          })
        } else {
          return false
        }
      })
    },
    async loadMenu ({ dispatch }, menuId) {
      const file = `menu/item/${menuId}.json`
      if (!menuId) return
      try {
        const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-file&siteid=${API.SITE_INFO.site}&file=${file}`)
        dispatch('setMenu', res.data.message)
        return res.data.message
      } catch (ex) {
        // console.log(ex.response)
        return
      }
    },
    async loadFloating ({ state, dispatch }) {
      const file = 'floating/floating.json'
      try {
        const res = await axios.get(`${API.BASE_URL}/castit/file?cmd=get-file&siteid=${API.SITE_INFO.site}&file=${file}`)
        state.floating = res.data.message
        return res.data.message
      } catch (ex) {
        return false
      }
    },
    setCommon ({ state, commit, dispatch }, data) {
      state.setting = { ...state.setting, ...data }
      state.lang = {
        default: data.lang.default,
        current: data.lang.default
      }
      
      // set added font list
      const setAddedFont = function (addedFonts) {
        addedFonts.forEach(function (font) {
          const fontFamilyId = `font_${font.family.trim()}`
          if (document.head.getElementsByClassName(fontFamilyId).length === 0) {
            const fontface = document.createElement('style')
            const createFontFace = document.createTextNode(`
              @font-face{
                font-family: ${font.family};
                src: url(${font.uri});
              }
            `)
            fontface.setAttribute('class', fontFamilyId)
            fontface.appendChild(createFontFace)
            document.head.appendChild(fontface)
          }
        })
      }
      commit('setUsableLang')
      commit('setMeta')
      setAddedFont(data.font.added)
      // document.body.style.fontFamily = `${data.font.default}, "Noto Sans", "Kosugi Maru", "Noto Sans SC", "Noto Sans KR", "sans-serif"`
      const defaultFonts = data.font.default + (data.font.default2 ? `, "${data.font.default2}"` : '') + (data.font.default3 ? `, "${data.font.default3}"` : '')
      const css = `body {
        font-family: ${defaultFonts}, "Noto Sans", "Kosugi Maru", "Noto Sans SC", "Noto Sans KR", "sans-serif"
      }`
      const head = document.head || document.getElementsByTagName('head')[0]
      const style = document.createElement('style')
      head.appendChild(style)
      style.type = 'text/css'
      if (style.styleSheet) {
        style.styleSheet.cssText = css
      } else {
        style.appendChild(document.createTextNode(css))
      }
      if (data.order && data.order.taxActive && data.order.tax.length > 0) {
        const tax = {
          key: 'tax',
          value: Number(data.order.tax)
        }
        store.commit('payment/setTaxTip', tax, { root: true })
      }
      if (data.order && data.order.tipActive) {
        const tip = {
          key: 'tip',
          value: [...data.order.tip, 'custom', 'notip'],
          includesTax: data.order.tipIncludeTax
        }
        store.commit('payment/setTaxTip', tip, { root: true })
      }
    },
    setMenu ({ state }, data) {
      state.menu.sns = data.snsList
      const filteredData = function (tree) {
        tree.forEach(function (x) {
          const parentId = x.data.id
          delete x.state
          delete x.text
          delete x.data.text
          if (x.children && x.children.length) {
            x.children = x.children.map(c => {
              return {
                ...c,
                parent: (x.parent || []).concat(x.data.id)
              }
            })
            filteredData(x.children)
          }
        })
        return tree
      }
      const list = filteredData(data.tree)
      state.menu.list = [...list]
    },
    setLang ({ state, commit, dispatch }, data) {
      const wasAuto = store.state.translate === 'auto'
      const isManual = state.setting.lang && state.setting.lang.manual && state.setting.lang.manual.includes(data)
      store.commit('translateRestore', isManual)
      setTimeout(() => {
        state.lang.current = data
        commit('setMeta')
        router.replace({ params: { ln: data } })
      }, wasAuto && isManual ? 500 : 0) // 자동번역에서 직접번역으로 바뀔때는 구글번역을 먼저 꺼야함
    },
    async setCustomResource ({ state, dispatch }) {
      const siteid = store.state.authentication.site
      dispatch('setCustomSitekey')
      await applySiteCustomCss(siteid)
    },
    async setCustomSitekey ({ state }) {
      const siteid = store.state.authentication.site
      const customKey = await getCustomSite(siteid)
      if (customKey) {
        state.customSiteKey = customKey
      }
    },

  },
  getters: {
    getCustomSiteKey: state => state.customSiteKey,
    getSetting: state => {
      return state.setting
    },
    getMenu: state => {
      return state.menu
    },
    getDateFormat (state) {
      if (typeof (state.setting.dateFormat) !== 'undefined') {
        return state.setting.dateFormat[state.setting.dateformatDefault]
      }
    },
    getTitle (state) {
      return state.setting.title[state.lang.current]
    },
    getLang (state) {
      return state.lang.current
    },
    getFloating: state => state.floating,
    getPointColors: state => (target, attr, alpha) => {
      const style = {}
      const value = state.setting && state.setting.pointcolors && state.setting.pointcolors[target]
      const setAlpha = (rgba, alpha) => {
        const match = /rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(,\s*\d+[\.\d+]*)*\)/g.exec(rgba)
        const a = alpha ? parseFloat(match[4]) / 100 * alpha : match[4]
        return `rgba(${[match[1],match[2],match[3],a].join(',')})`
      }
      style[attr] = alpha ? setAlpha(value, alpha) : value
      return attr && value ? style : null
    },
    getPointColorsMultiple: state => (arr) => {
      const style = {}
      for (let i = 0; i < arr.length; i++) {
        const st = arr[i]
        const target = st.target
        const attr = st.attr
        const value = state.setting && state.setting.pointcolors && state.setting.pointcolors[target]
        if (target && attr && value) {
          style[attr] = value
        }
      }
      return arr && arr.length > 0 ? style : null
    },
    getUserinfo: state => {
      const order = state.setting.order
      const custom = Object.keys(order).filter(x => x.startsWith('custom') && order[x].active).map(x => order[x])
      return {
        firstName: order && order.firstName === false ? order.firstName : true,
        lastName: order && order.lastName === false ? order.lastName : true,
        tel: order ? order.tel : true,
        email: order ? order.email : false,
        ...custom && {
          custom: custom
        }
      }
    },
    getDefaultBtnStyle: state => {
      const colors = state.setting.pointcolors
      const hasColor = colors.defaultBtnBg && colors.defaultBtnText
      return hasColor && {
        borderColor: colors.defaultBtnBg,
        backgroundColor: colors.defaultBtnBg,
        color: colors.defaultBtnText
      }
    },
    getDefaultBtnStyleReverse: state => {
      const colors = state.setting.pointcolors
      const hasColor = colors.defaultBtnBg && colors.defaultBtnText
      return hasColor && {
        borderColor: colors.defaultBtnBg,
        backgroundColor: colors.defaultBtnText,
        color: colors.defaultBtnBg
      }
    },
    getDefaultBtn2Style: state => {
      const colors = state.setting.pointcolors
      const hasColor = colors.defaultBtn2Bg && colors.defaultBtn2Text
      return hasColor && {
        borderColor: colors.defaultBtn2Bg,
        backgroundColor: colors.defaultBtn2Bg,
        color: colors.defaultBtn2Text
      }
    },
  }
}
